import { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { Menu, Plus, Settings, Info } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { type ChatAttachmentRef, type ChatMemberPermissions } from '@/lib/api';
import { useChat } from '../hooks';
import { useChatChannel } from '../hooks/useChatChannel';
import { useChatSettings } from '../hooks/useChatSettings';
import { ChannelList } from '../components/ChannelList';
import { ChannelHeader } from '../components/ChannelHeader';
import { MessageList } from '../components/MessageList';
// MessageInput pulls in TipTap + ProseMirror (~150 KB). Code-split it out of
// the main bundle so users who never open the chat arche don't pay the cost.
const MessageInput = lazy(() =>
  import('../components/MessageInput').then((m) => ({ default: m.MessageInput })),
);
import { TypingIndicator } from '../components/TypingIndicator';
import { ChatSettingsModal } from '../components/ChatSettingsModal';
import { NewChatDialog } from '../components/NewChatDialog';
import { useAuth } from '@/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { useSidebar } from '../../../context/SidebarContext';

interface ChatPageProps {
  /** Channel ID to use when none is provided via URL params (e.g. /chat route) */
  defaultChannelId?: string;
}

export function ChatPage({ defaultChannelId }: ChatPageProps = {}) {
  const { channelId: urlChannelId } = useParams<{ channelId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const effectiveChannelId = urlChannelId || defaultChannelId || null;
  const [activeChannelId, setActiveChannelId] = useState<string | null>(effectiveChannelId);

  // Sync activeChannelId when the URL param or default changes (e.g. browser back/forward)
  useEffect(() => {
    setActiveChannelId(effectiveChannelId);
  }, [effectiveChannelId]);

  const { channels, messages, typing } = useChat(activeChannelId);
  const { channel: activeChannel, members, memberCount } = useChatChannel(activeChannelId);

  // Stable refs to avoid re-creating callbacks when messages/channels change
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  const handleSelectChannel = useCallback(
    (channelId: string) => {
      setActiveChannelId(channelId);
      navigate(`/a/arche_chat/channel/${channelId}`, { replace: true });
    },
    [navigate],
  );

  const handleCreateDM = useCallback(
    async (targetUserId: string) => {
      const channel = await channelsRef.current.openDirectMessage(targetUserId);
      if (channel) handleSelectChannel(channel.channelId);
    },
    [handleSelectChannel],
  );

  const handleCreateGroup = useCallback(
    async (name: string, description: string) => {
      const channel = await channelsRef.current.createChannel({
        channelType: 'group',
        name,
        description: description || undefined,
      });
      if (channel) handleSelectChannel(channel.channelId);
    },
    [handleSelectChannel],
  );

  // Edit state — lifted here so MessageInput can take over the edit flow
  const [editingMessage, setEditingMessage] = useState<{
    messageId: string;
    content: string;
  } | null>(null);

  // Reply state — lifted here so MessageInput shows the reply banner
  const [replyTo, setReplyTo] = useState<{
    messageId: string;
    senderName: string;
    content: string;
  } | null>(null);

  const handleSendMessage = useCallback(
    async (content: string, replyToId?: string, attachmentRefs?: ChatAttachmentRef[]) => {
      const sent = await messagesRef.current.sendMessage(content, replyToId, attachmentRefs);
      if (sent && activeChannelId) {
        channelsRef.current.bumpChannel(activeChannelId, {
          content: sent.content,
          senderId: sent.senderId,
          senderName: sent.senderName ?? null,
          createdAt: sent.createdAt,
        });
      }
    },
    [activeChannelId],
  );

  const handleStartEdit = useCallback((messageId: string) => {
    const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
    if (msg) setEditingMessage({ messageId, content: msg.content });
  }, []);

  const handleSaveEdit = useCallback(async (messageId: string, content: string) => {
    await messagesRef.current.editMessage(messageId, content);
    setEditingMessage(null);
  }, []);

  const handleDeleteMessage = useCallback((messageId: string) => {
    messagesRef.current.deleteMessage(messageId);
  }, []);

  const handleReply = useCallback((messageId: string) => {
    const msg = messagesRef.current.messages.find((m) => m.messageId === messageId);
    if (!msg) return;
    setReplyTo({
      messageId,
      senderName: msg.senderName || msg.senderId || 'Unknown',
      content: msg.content,
    });
  }, []);

  const handleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      const msgs = messagesRef.current.messages;
      const msg = msgs.find((m) => m.messageId === messageId);
      if (!msg || !user) return;

      const existing = msg.reactions?.find((r) => r.emoji === emoji);
      const alreadyReacted = existing?.userIds.includes(user.userId);

      // Optimistic update
      messagesRef.current.updateMessage(messageId, {
        reactions: alreadyReacted
          ? (msg.reactions ?? [])
              .map((r) =>
                r.emoji === emoji
                  ? {
                      ...r,
                      count: r.count - 1,
                      userIds: r.userIds.filter((id) => id !== user.userId),
                    }
                  : r,
              )
              .filter((r) => r.count > 0)
          : existing
            ? (msg.reactions ?? []).map((r) =>
                r.emoji === emoji
                  ? { ...r, count: r.count + 1, userIds: [...r.userIds, user.userId] }
                  : r,
              )
            : [...(msg.reactions ?? []), { emoji, count: 1, userIds: [user.userId] }],
      });

      try {
        if (alreadyReacted) {
          await api.removeChatReaction(messageId, emoji);
        } else {
          await api.addChatReaction(messageId, emoji);
        }
      } catch {
        // Revert optimistic update on failure
        messagesRef.current.updateMessage(messageId, { reactions: msg.reactions });
      }
    },
    [user],
  );

  // Get current user ID and admin status. Chat is platform-wide (not a
  // litcraft-domain arche), so this gate is platform `admin` only —
  // `litcraft_admin` does NOT get cross-channel delete-anyone authority,
  // matching the server-side `requireAdmin` middleware on the chat
  // moderation routes. Channel-level role admin (`isChannelAdmin` below)
  // is a separate concept — it's the per-channel `member.role` and is
  // unrelated to platform roles.
  const currentUserId = user?.userId || null;
  const isAdmin = user?.roles?.includes('admin') ?? false;

  // Compute effective channel-level permissions for the current member.
  // Admins (owner/admin role) bypass all member restrictions.
  // Individual permissions override channel defaults; unset keys default to allowed.
  const currentChannelMember = members.find((m) => m.userId === currentUserId) ?? null;
  const isChannelAdmin =
    currentChannelMember?.role === 'owner' || currentChannelMember?.role === 'admin';

  function resolveChannelPerm(key: keyof ChatMemberPermissions): boolean {
    if (isChannelAdmin) return true;
    const individual = currentChannelMember?.permissions?.[key];
    if (individual !== undefined) return individual !== false;
    const def = activeChannel?.defaultPermissions?.[key];
    return def !== false;
  }

  const messagePermissions = {
    canSendMessages: resolveChannelPerm('sendMessages'),
    canSendImages: resolveChannelPerm('sendImages'),
    canSendVideos: resolveChannelPerm('sendVideos'),
    canSendFiles: resolveChannelPerm('sendFiles'),
    canSendLinks: resolveChannelPerm('sendLinks'),
  };
  const { isCollapsed, toggle } = useSidebar();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Background from user settings
  const { settings: chatSettings } = useChatSettings();
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!chatSettings.backgroundImageId) {
      setBgImageUrl(null);
      return;
    }
    let cancelled = false;
    api
      .getDownloadUrl(chatSettings.backgroundImageId)
      .then((r) => {
        if (!cancelled) setBgImageUrl(r.downloadUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chatSettings.backgroundImageId]);

  const msgAreaBgStyle: React.CSSProperties = bgImageUrl
    ? {
        backgroundImage: `url(${bgImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : chatSettings.backgroundColor
      ? { backgroundColor: chatSettings.backgroundColor }
      : {};

  // On mobile, show either the sidebar or the message pane — not both.
  const showMessagePane = !!activeChannelId;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Channel Sidebar
          Mobile: full-screen when no channel is selected, hidden when one is.
          Desktop: fixed 420px, always visible. */}
      <div
        className={[
          'relative flex-col flex-shrink-0 border-r border-border bg-card]',
          'md:flex md:w-[420px] md:min-w-[420px]',
          showMessagePane ? 'hidden' : 'flex w-full',
        ].join(' ')}
      >
        <div className="border-b border-border px-4 flex items-center justify-between !h-[55px] min-h-[55px]">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              className={`h-9 w-9 flex-shrink-0 bg-gray-100 dark:bg-zinc-700 ${isCollapsed ? '' : 'invisible'}`}
              title="Open sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold text-foreground">Chat</h1>
            <Link
              to="/marketplace/arches/arche_chat"
              className="text-muted-foreground hover:text-primary transition-colors"
              title="View arche details"
            >
              <Info className="h-5 w-5" />
            </Link>
            <FavoriteButton archeId="arche_chat" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsOpen(true)}
            title="Chat settings"
            className="h-8 w-8"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
        <ChannelList
          channels={channels.channels}
          activeChannelId={activeChannelId}
          onSelectChannel={handleSelectChannel}
          loading={channels.loading}
          viewMode={chatSettings.appearanceChannelListView}
        />

        {/* Floating action button — new DM / group */}
        <div className="absolute bottom-4 right-3">
          <NewChatDialog
            onCreateDM={handleCreateDM}
            onCreateGroup={handleCreateGroup}
            trigger={
              <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all">
                <Plus className="h-5 w-5" />
              </div>
            }
          />
        </div>
      </div>

      {/* Message Area
          Mobile: full-screen when a channel is selected, hidden otherwise.
          Desktop: fills remaining space, always visible. */}
      <div
        className={[
          'flex-1 flex-col min-w-0 overflow-hidden',
          showMessagePane ? 'flex' : 'hidden md:flex',
        ].join(' ')}
      >
        <ChannelHeader
          channel={activeChannel}
          memberCount={memberCount}
          currentUserId={currentUserId}
          members={members}
          onBack={() => setActiveChannelId(null)}
          onChannelLeft={() => {
            if (activeChannel?.channelType === 'direct') {
              setActiveChannelId(null);
              navigate('/a/arche_chat', { replace: true });
            }
            // Groups/guilds stay selected in read-only mode so the user can view history or rejoin
          }}
          onChannelDeleted={() => {
            setActiveChannelId(null);
            navigate('/a/arche_chat', { replace: true });
          }}
        />
        <div className="relative flex-1 min-h-0 overflow-hidden">
          {/* Background layer */}
          {(bgImageUrl || chatSettings.backgroundColor) && (
            <div
              className="absolute inset-0 z-0"
              style={{
                ...msgAreaBgStyle,
                filter: bgImageUrl && chatSettings.backgroundBlur ? 'blur(8px)' : undefined,
                transform: bgImageUrl && chatSettings.backgroundBlur ? 'scale(1.05)' : undefined,
              }}
            />
          )}
          <div className="relative z-10 h-full flex flex-col">
            <MessageList
              messages={messages.messages}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              loading={messages.loading}
              loadingMore={messages.loadingMore}
              hasMore={messages.hasMore}
              onLoadMore={messages.loadMore}
              onStartEditMessage={handleStartEdit}
              onDeleteMessage={handleDeleteMessage}
              onReplyMessage={handleReply}
              onReactionMessage={handleReaction}
              onOpenDm={handleCreateDM}
            />
          </div>
        </div>
        <TypingIndicator
          typingUsers={typing.typingUsers.filter((u) => u.userId !== currentUserId)}
        />
        {activeChannelId && (
          <Suspense
            fallback={
              <div className="border-t border-border p-3 text-xs text-muted-foreground">
                Loading composer…
              </div>
            }
          >
            <MessageInput
              onSend={handleSendMessage}
              onSaveEdit={handleSaveEdit}
              onDeleteMessage={handleDeleteMessage}
              onTyping={typing.sendTyping}
              members={members}
              disabled={!activeChannelId || !!activeChannel?.hasLeft}
              leftChannel={!!activeChannel?.hasLeft}
              placeholder={
                activeChannel?.hasLeft
                  ? 'You left this channel'
                  : activeChannel
                    ? `Message ${activeChannel.dmPartnerDisplayName || activeChannel.name || 'channel'}...`
                    : 'Select a channel...'
              }
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              editingMessage={editingMessage}
              onCancelEdit={() => setEditingMessage(null)}
              permissions={messagePermissions}
            />
          </Suspense>
        )}
      </div>

      <ChatSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
